(() => {
  'use strict';

  const money = (cents, symbol) => `${(Math.round(cents) / 100).toFixed(2).replace('.', ',')}${symbol || '€'}`;

  const DESKTOP = '(min-width: 990px)';

  class PackBuilder extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.symbol = this.dataset.pbCurrency || '€';
      this.minItems = parseInt(this.dataset.pbMin, 10) || 1;
      this.storageKey = this.dataset.pbStorageKey || 'pb:default';
      this.giftLabel = this.dataset.pbGiftLabel || 'Offert';

      this.template = this.querySelector('[data-pb-line-template]');
      this.sticky = this.querySelector('[data-pb-sticky]');
      this.panel = this.querySelector('[data-pb-panel]');
      this.body = this.querySelector('.pb__body');
      this.tierbar = this.querySelector('[data-pb-tierbar]');
      this.drawer = this.querySelector('w-drawer');

      this.registry = new Map();
      this.cards = Array.from(this.querySelectorAll('[data-pb-card]'));
      this.cards.forEach((card) => this.indexCard(card));

      this.state = this.restore();
      this.rewards = this.readRewards();

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener(
        'change',
        (event) => {
          const choix = event.target.closest('[data-pb-choice]');
          if (choix && this.contains(choix)) this.choiceVariant = Number(choix.value) || null;
        },
        { signal }
      );

      this.drawer?.addEventListener('w-drawer:open', () => this.renderTiers(), { signal });

      let resizeTimer = null;
      window.addEventListener(
        'resize',
        () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            this.observeReference();
            this.renderTiers();
          }, 150);
        },
        { signal }
      );

      this.observeReference();
      this.render();
      this.initPaymentPills();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }

    indexCard(card) {
      const holder = card.querySelector('[data-pb-card-data]');
      if (!holder) return;

      let data = null;
      try {
        data = JSON.parse(holder.textContent);
      } catch (error) {
        return;
      }
      if (!data || !Array.isArray(data.variants)) return;

      data.variants.forEach((variant) => {
        this.registry.set(String(variant.id), {
          id: variant.id,
          label: variant.label || '',
          sub: variant.sub || '',
          price: Number(variant.price) || 0,
          compare: Number(variant.compare) || 0,
          available: variant.available !== false,
          title: data.title || '',
          url: data.url || '',
          image: data.image || '',
          card,
        });
      });
    }

    restore() {
      const state = new Map();
      let raw = null;
      try {
        raw = window.localStorage.getItem(this.storageKey);
      } catch (error) {
        return state;
      }
      if (!raw) return state;

      let parsed = null;
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        return state;
      }
      if (!parsed || typeof parsed !== 'object') return state;

      Object.keys(parsed).forEach((id) => {
        const entry = this.registry.get(String(id));
        const qty = parseInt(parsed[id], 10);
        if (entry && entry.available && qty > 0) state.set(String(id), qty);
      });
      return state;
    }

    persist() {
      const plain = {};
      this.state.forEach((qty, id) => {
        plain[id] = qty;
      });
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(plain));
      } catch (error) {
        }
    }

    get count() {
      let total = 0;
      this.state.forEach((qty) => {
        total += qty;
      });
      return total;
    }

    valeurPour(base) {
      return base === 'amount' ? this.total : this.count;
    }

    get total() {
      let cents = 0;
      this.state.forEach((qty, id) => {
        const entry = this.registry.get(id);
        if (entry) cents += entry.price * qty;
      });
      return cents;
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;

      const format = event.target.closest('[data-pb-format]');
      if (format) return this.selectFormat(format);

      const add = event.target.closest('[data-pb-add]');
      if (add) return this.changeCard(add.closest('[data-pb-card]'), 1);

      const step = event.target.closest('[data-pb-step]');
      if (step) return this.changeCard(step.closest('[data-pb-card]'), parseInt(step.dataset.pbStep, 10));

      const lineStep = event.target.closest('[data-pb-line-step]');
      if (lineStep) {
        const line = lineStep.closest('[data-pb-line-id]');
        return this.change(line?.dataset.pbLineId, parseInt(lineStep.dataset.pbLineStep, 10));
      }

      const remove = event.target.closest('[data-pb-line-remove]');
      if (remove) {
        const line = remove.closest('[data-pb-line-id]');
        return this.change(line?.dataset.pbLineId, -Infinity);
      }

      const submit = event.target.closest('[data-pb-submit]');
      if (submit) return this.submit(submit);

      const edit = event.target.closest('[data-pb-edit]');
      if (edit) return this.edit();
    }

    selectFormat(button) {
      const card = button.closest('[data-pb-card]');
      if (!card) return;
      card.dataset.pbVariant = button.dataset.pbFormat;
      card.querySelectorAll('[data-pb-format]').forEach((pill) => {
        const on = pill === button;
        pill.classList.toggle('is-selected', on);
        pill.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.renderCard(card);
    }

    changeCard(card, delta) {
      if (!card) return;
      this.change(card.dataset.pbVariant, delta);
    }

    change(variantId, delta) {
      const id = String(variantId || '');
      const entry = this.registry.get(id);
      if (!entry) return;

      if (delta === -Infinity) {
        this.state.delete(id);
      } else {
        const next = (this.state.get(id) || 0) + delta;
        if (next > 0) this.state.set(id, next);
        else this.state.delete(id);
      }

      this.persist();
      this.render();
    }

    edit() {
      if (window.matchMedia(DESKTOP).matches) {
        this.panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (typeof this.drawer?.open === 'function') this.drawer.open();
    }

    readRewards() {
      const node = this.querySelector('[data-pb-rewards]');
      if (!node) return [];
      try {
        const parsed = JSON.parse(node.textContent || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((r) => r && (r.variant > 0 || (Array.isArray(r.items) && r.items.length > 0)));
      } catch (error) {
        return [];
      }
    }

    recompenseAtteinte() {
      let atteint = null;
      this.rewards.forEach((reward) => {
        if (this.valeurPour(reward.basis) >= (reward.threshold || 0)) atteint = reward;
      });
      return atteint;
    }

    giftChoice() {
      const r = this.recompenseAtteinte();
      const c = r && r.choice;
      if (!c || !Array.isArray(c.options) || c.options.length === 0) return null;
      return c;
    }

    giftEntries() {
      let atteint = null;
      this.rewards.forEach((reward) => {
        const valeur = this.valeurPour(reward.basis);
        if (valeur >= (reward.threshold || 0)) atteint = reward;
      });
      if (!atteint) return [];

      const qty = Math.max(1, Number(atteint.quantity) || 1);
      const items = Array.isArray(atteint.items)
        ? atteint.items
        : (atteint.variant ? [{ variant: atteint.variant }] : []);

      const parVariant = new Map();
      items.forEach((item) => {
        const id = Number(item.variant);
        if (!id || parVariant.has(id)) return;
        parVariant.set(id, {
          id,
          quantity: qty,
          title: item.title || '',
          url: item.url || '',
          image: item.image || '',
          label: item.label || '',
        });
      });
      return Array.from(parVariant.values());
    }


    async submit(button) {
      if (this.count < this.minItems) return;

      const items = [];
      this.state.forEach((qty, id) => items.push({ id: Number(id), quantity: qty }));
      if (items.length === 0) return;

      const choix = this.giftChoice();
      if (choix && this.choiceVariant) {
        const retenu = choix.options.find((o) => String(o.id) === String(this.choiceVariant));
        if (retenu) {
          const cle = choix.label || 'Cadeau au choix';
          items[0].properties = Object.assign({}, items[0].properties, { [cle]: retenu.label });
        }
      }

      const routes = window.routes || {};
      const url = routes.cart_add_url ? `${routes.cart_add_url}.js` : '/cart/add.js';

      this.clearError();
      this.setBusy(true);
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: 'w-side-cart', sections_url: window.location.pathname }),
        });
        const json = await response.json().catch(() => null);
        if (!response.ok) throw new Error(json?.description || `cart/add ${response.status}`);

        this.state.clear();
        this.persist();
        this.render();
        this.drawer?.close?.();

        document.dispatchEvent(
          new CustomEvent('cart:add', { detail: { sections: json?.sections, open: true } })
        );
      } catch (error) {
        this.showError(button);
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      const enabled = this.count >= this.minItems;
      this.querySelectorAll('[data-pb-submit]').forEach((el) => {
        el.classList.toggle('is-busy', busy);
        if (busy) el.setAttribute('aria-busy', 'true');
        else el.removeAttribute('aria-busy');
        el.disabled = busy ? true : !enabled;
      });
    }

    clearError() {
      this.querySelectorAll('[data-pb-error-node]').forEach((el) => el.remove());
    }

    showError(button) {
      const message = this.dataset.pbError;
      if (!message) return;
      const host = button?.closest('.pb-summary__foot') || button?.parentElement;
      if (!host) return;
      let node = host.querySelector('[data-pb-error-node]');
      if (!node) {
        node = document.createElement('p');
        node.className = 'pb-summary__error';
        node.setAttribute('role', 'alert');
        node.setAttribute('data-pb-error-node', '');
        host.appendChild(node);
      }
      node.textContent = message;
    }

    render() {
      this.cards.forEach((card) => this.renderCard(card));
      this.renderLines();
      this.renderTiers();
      this.renderTotals();
      this.renderSticky();
    }

    renderCard(card) {
      const qty = this.state.get(String(card.dataset.pbVariant)) || 0;
      card.dataset.pbQty = String(qty);

      const value = card.querySelector('[data-pb-step-value]');
      if (value) value.textContent = String(qty);

      const stepper = card.querySelector('[data-pb-stepper]');
      if (stepper) stepper.setAttribute('aria-hidden', qty === 0 ? 'true' : 'false');

      const entry = this.registry.get(String(card.dataset.pbVariant));
      if (!entry) return;

      const price = card.querySelector('[data-pb-card-price]');
      if (price) price.textContent = money(entry.price, this.symbol);

      const unit = card.querySelector('[data-pb-card-unit]');
      if (unit) {
        const grams = parseInt(String(entry.label).replace(/[^\d]/g, ''), 10);
        if (grams > 0) unit.textContent = `(${money(entry.price / grams, this.symbol)} /g)`;
        else unit.textContent = '';
      }
    }

    renderLines() {
      if (!this.template) return;

      const lists = this.querySelectorAll('[data-pb-lines]');
      lists.forEach((list) => {
        const empty = list.querySelector('[data-pb-empty]');
        list.querySelectorAll('[data-pb-line-id]').forEach((node) => node.remove());

        const fragment = document.createDocumentFragment();
        this.state.forEach((qty, id) => {
          const entry = this.registry.get(id);
          if (!entry) return;
          fragment.appendChild(this.buildLine(entry, qty));
        });
        this.giftEntries().forEach((cadeau) => {
          fragment.appendChild(this.buildGiftLine(cadeau));
        });
        const choix = this.giftChoice();
        if (choix) fragment.appendChild(this.buildChoiceLine(choix));
        list.appendChild(fragment);

        if (empty) empty.hidden = this.state.size > 0;
      });
    }

    buildChoiceLine(choix) {
      const node = this.buildGiftLine({
        id: 'choice',
        quantity: 1,
        title: choix.title,
        url: choix.url,
        image: choix.image,
        label: '',
      });
      node.dataset.pbLineId = 'gift-choice';
      node.dataset.pbGiftChoice = '';

      const dispo = choix.options.map((o) => String(o.id));
      if (!dispo.includes(String(this.choiceVariant))) this.choiceVariant = choix.options[0].id;

      const sub = node.querySelector('[data-pb-line-sub]');
      if (sub) {
        sub.textContent = '';
        const select = document.createElement('select');
        select.className = 'pb-line__choice';
        select.setAttribute('data-pb-choice', '');
        select.setAttribute('aria-label', choix.label || 'Cadeau au choix');
        choix.options.forEach((o) => {
          const opt = document.createElement('option');
          opt.value = String(o.id);
          opt.textContent = o.label;
          if (String(o.id) === String(this.choiceVariant)) opt.selected = true;
          select.appendChild(opt);
        });
        sub.appendChild(select);
      }
      return node;
    }

    buildGiftLine(cadeau) {
      const node = this.template.content.firstElementChild.cloneNode(true);
      node.dataset.pbLineId = `gift-${cadeau.id}`;
      node.dataset.pbGift = '';

      const href = node.querySelector('[data-pb-line-href]');
      if (href) href.href = cadeau.url || '#';

      const img = node.querySelector('[data-pb-line-img]');
      if (img) {
        if (cadeau.image) img.src = cadeau.image;
        else img.remove();
      }

      const title = node.querySelector('[data-pb-line-title]');
      if (title) {
        title.textContent = cadeau.title;
        title.href = cadeau.url || '#';
      }

      const sub = node.querySelector('[data-pb-line-sub]');
      if (sub) {
        sub.textContent = '';
        if (cadeau.label) {
          const b = document.createElement('b');
          b.textContent = cadeau.label;
          sub.appendChild(b);
        }
      }

      const compare = node.querySelector('[data-pb-line-compare]');
      if (compare) compare.hidden = true;

      const price = node.querySelector('[data-pb-line-price]');
      if (price) price.textContent = this.giftLabel;

      const qtyNode = node.querySelector('[data-pb-line-qty]');
      if (qtyNode) qtyNode.textContent = String(cadeau.quantity);

      node.querySelectorAll('[data-pb-line-step]').forEach((b) => b.remove());
      node.querySelectorAll('[data-pb-line-remove]').forEach((b) => b.remove());

      // Un cadeau ne se clique pas : on neutralise les liens de la ligne.
      node.querySelectorAll('a[data-pb-line-href], a[data-pb-line-title]').forEach((a) => {
        a.removeAttribute('href');
        a.style.cursor = 'default';
        a.style.pointerEvents = 'none';
      });

      return node;
    }

    buildLine(entry, qty) {
      const node = this.template.content.firstElementChild.cloneNode(true);
      node.dataset.pbLineId = String(entry.id);

      const href = node.querySelector('[data-pb-line-href]');
      if (href) href.href = entry.url;

      const img = node.querySelector('[data-pb-line-img]');
      if (img) {
        if (entry.image) img.src = entry.image;
        else img.remove();
      }

      const title = node.querySelector('[data-pb-line-title]');
      if (title) {
        title.textContent = entry.title;
        title.href = entry.url;
      }

      const sub = node.querySelector('[data-pb-line-sub]');
      if (sub) {
        sub.textContent = '';
        if (entry.label) {
          const strong = document.createElement('b');
          strong.textContent = entry.label;
          sub.appendChild(strong);
        }
        if (entry.sub) sub.appendChild(document.createTextNode(` (${entry.sub})`));
      }

      const compare = node.querySelector('[data-pb-line-compare]');
      if (compare) {
        const show = entry.compare > entry.price;
        compare.hidden = !show;
        compare.textContent = show ? money(entry.compare, this.symbol) : '';
      }

      const price = node.querySelector('[data-pb-line-price]');
      if (price) price.textContent = money(entry.price, this.symbol);

      const qtyNode = node.querySelector('[data-pb-line-qty]');
      if (qtyNode) qtyNode.textContent = String(qty);

      return node;
    }

    renderTiers() {
      this.querySelectorAll('[data-pb-tiers]').forEach((bar) => {
        const items = Array.from(bar.querySelectorAll('[data-pb-tier]'));
        if (items.length === 0) return;

        const bases = items.map((item) => (item.dataset.pbBasis === 'amount' ? 'amount' : 'items'));
        const thresholds = items.map((item) => parseInt(item.dataset.pbThreshold, 10) || 0);
        const valeurs = bases.map((base) => this.valeurPour(base));

        items.forEach((item, i) => {
          item.dataset.pbReached = valeurs[i] >= thresholds[i] ? 'true' : 'false';
        });

        const fill = bar.querySelector('[data-pb-fill]');
        if (!fill) return;

        const barRect = bar.getBoundingClientRect();
        if (barRect.width === 0) return;

        const centers = items.map((item) => {
          const rect = item.getBoundingClientRect();
          return ((rect.left + rect.width / 2 - barRect.left) / barRect.width) * 100;
        });

        bar.style.setProperty('--pb-fill', `${this.fillPercent(valeurs, thresholds, bases, centers)}%`);
      });
    }

    fillPercent(valeurs, thresholds, bases, centers) {
      const last = thresholds.length - 1;
      const atteint = (i) => thresholds[i] > 0 && valeurs[i] >= thresholds[i];
      const ratio = (i) => (thresholds[i] > 0 ? Math.min(1, Math.max(0, valeurs[i] / thresholds[i])) : 0);

      if (atteint(last)) return 100;

      let dernier = -1;
      for (let i = 0; i <= last; i += 1) if (atteint(i)) dernier = i;

      if (dernier < 0) return Math.max(0, centers[0] * ratio(0));

      const suivant = dernier + 1;
      const depart = centers[dernier];
      const arrivee = centers[suivant];

      if (bases[dernier] === bases[suivant]) {
        const span = thresholds[suivant] - thresholds[dernier];
        if (span <= 0) return depart;
        const avance = Math.min(1, Math.max(0, (valeurs[suivant] - thresholds[dernier]) / span));
        return depart + (arrivee - depart) * avance;
      }

      return depart + (arrivee - depart) * ratio(suivant);
    }

    initPaymentPills(tries) {
      const pistes = this.querySelectorAll('[data-pp-swiper]');
      if (!pistes.length) return;
      if (typeof window.Swiper === 'undefined') {
        if ((tries || 0) > 100) return;
        setTimeout(() => this.initPaymentPills((tries || 0) + 1), 50);
        return;
      }
      pistes.forEach((el) => {
        if (el.swiper) return;
        const dots = el.dataset.ppSwiperPagination
          ? this.querySelector(el.dataset.ppSwiperPagination)
          : null;
        const autoplay = Number(el.dataset.ppSwiperAutoplay);
        el.swiper = new window.Swiper(el, {
          slidesPerView: 1,
          spaceBetween: Number(el.dataset.ppSwiperGap) || 12,
          watchOverflow: true,
          resizeObserver: true,
          loop: el.dataset.ppSwiperLoop === 'true',
          pagination: dots
            ? { el: dots, clickable: true, bulletClass: 'pp-dot', bulletActiveClass: 'is-active' }
            : false,
          autoplay: autoplay > 0 ? { delay: autoplay, disableOnInteraction: false, pauseOnMouseEnter: true } : false,
        });
      });
    }

    renderTotals() {
      const total = this.total;
      const enabled = this.count >= this.minItems;

      this.querySelectorAll('[data-pb-total]').forEach((el) => (el.textContent = money(total, this.symbol)));
      this.querySelectorAll('[data-pb-third]').forEach((el) => {
        const split = Number(el.dataset.ppKlarnaSplit) || 3;
        el.textContent = money(total / split, this.symbol);
      });
      this.querySelectorAll('[data-pb-submit]').forEach((el) => (el.disabled = !enabled));
    }

    renderSticky() {
      if (!this.sticky) return;

      const count = this.count;
      const visible = count > 0 && this.referencePassed === true;

      const label = this.sticky.querySelector('[data-pb-sticky-count]');
      if (label) {
        const pattern = count === 1 ? this.dataset.pbCountOne : this.dataset.pbCountMany;
        label.textContent = (pattern || '').replace('[n]', String(count));
      }

      const thumb = this.sticky.querySelector('[data-pb-sticky-thumb]');
      if (thumb) {
        const first = this.state.keys().next();
        const entry = first.done ? null : this.registry.get(first.value);
        if (entry && entry.image) {
          thumb.src = entry.image;
          thumb.hidden = false;
        } else {
          thumb.hidden = true;
        }
      }

      this.style.setProperty('--pb-sticky-space', visible ? `${this.sticky.offsetHeight}px` : '0px');

      if (visible) {
        this.sticky.hidden = false;
        void this.sticky.offsetHeight;
        this.sticky.classList.add('is-visible');
      } else {
        this.sticky.classList.remove('is-visible');
        if (count === 0) this.sticky.hidden = true;
      }
    }

    observeReference() {
      this.observer?.disconnect();
      const reference = window.matchMedia(DESKTOP).matches ? this.body : this.tierbar;
      if (!reference || typeof window.IntersectionObserver !== 'function') {
        this.referencePassed = false;
        this.renderSticky();
        return;
      }
      this.referencePassed = reference.getBoundingClientRect().bottom < 0;
      this.observer = new window.IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          this.referencePassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.renderSticky();
        },
        { threshold: 0 }
      );
      this.observer.observe(reference);
    }
  }

  if (!window.customElements.get('pack-builder')) {
    window.customElements.define('pack-builder', PackBuilder);
  }
})();
